import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StudentLayout from "@/components/StudentLayout";
import api, { apiErr, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, XCircle, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TuitionAssessment() {
  const { sessionId, attemptId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);  // null = loading
  const [meta, setMeta] = useState({ subject: "", lessons: [] });
  const [answers, setAnswers] = useState({});         // {q_id: idx}
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [bootError, setBootError] = useState("");

  // Review mode: open a PAST submitted practice test by its attempt id.
  useEffect(() => {
    if (!attemptId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/tuition/assessment/attempt/${attemptId}`);
        if (alive) setResult(data);
      } catch (e) {
        if (alive) { setBootError(apiErr(e.response?.data?.detail) || "Could not load this practice test."); setQuestions([]); }
      }
    })();
    return () => { alive = false; };
  }, [attemptId]);

  // The questions are already created server-side; we need the bundle
  // that came back from /generate. We didn't ship it in URL state, so when
  // refreshing the page we trigger a friendly empty state — student goes back.
  useEffect(() => {
    if (attemptId) return;  // review mode handled above
    let alive = true;
    (async () => {
      try {
        // session_id is the only handle we have here; the original /generate
        // response is what carries the questions. We persist a small copy
        // in sessionStorage so a back/forward navigation still works.
        const cached = sessionStorage.getItem(`tassess:${sessionId}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (alive) {
            setQuestions(parsed.questions);
            setMeta({ subject: parsed.subject, lessons: parsed.lessons });
          }
          return;
        }
        if (alive) {
          setBootError("Assessment session was not found. Please start a new one from the Tuitions page.");
          setQuestions([]);
        }
      } catch (e) {
        if (alive) setBootError(apiErr(e) || "Could not load assessment.");
      }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  const allAnswered = useMemo(
    () => questions && questions.length > 0 && questions.every((q) => answers[q.id] !== undefined),
    [questions, answers],
  );

  const submit = async () => {
    if (!allAnswered || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/tuition/assessment/submit", {
        session_id: sessionId,
        answers,
      });
      setResult(data);
      sessionStorage.removeItem(`tassess:${sessionId}`);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <ResultScreen result={result} navigate={navigate} />;
  }

  if (questions === null) {
    return (
      <StudentLayout>
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading assessment…</div>
      </StudentLayout>
    );
  }

  if (bootError) {
    return (
      <StudentLayout>
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate("/student/tuitions")} className="mb-4"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Tuitions</Button>
          <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300 px-4 py-3 text-sm" data-testid="tassess-boot-error">
            {bootError}
          </div>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate("/student/tuitions")} className="mb-4" data-testid="tassess-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Quick assessment</div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
            {meta.subject}
          </h1>
          <div className="text-sm text-slate-500 mt-0.5">
            {meta.lessons?.map((l) => l.title).join(" · ") || ""}
          </div>
        </div>

        <ol className="space-y-5">
          {questions.map((q, i) => (
            <li key={q.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`tassess-q-${i}`}>
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
                <span className="text-slate-400 mr-2">{i + 1}.</span>{q.prompt}
              </div>
              {q.figure_image && (
                <img src={`${API_BASE}${q.figure_image}`} alt="figure" className="mb-3 max-w-[300px] max-h-[240px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white" />
              )}
              <div className="space-y-2">
                {q.options.map((opt, idx) => {
                  const selected = answers[q.id] === idx;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setAnswers((a) => ({ ...a, [q.id]: idx }))}
                      className={
                        "w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors " +
                        (selected
                          ? "border-sky-300 bg-sky-50 dark:bg-sky-900/30 dark:border-sky-700"
                          : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900")
                      }
                      data-testid={`tassess-q-${i}-opt-${idx}`}
                    >
                      <div className={`h-4 w-4 rounded-full border-2 shrink-0 ${selected ? "border-sky-500 bg-sky-500" : "border-slate-300 dark:border-slate-600"}`} />
                      <div className="text-sm">{opt}</div>
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-6 flex items-center justify-end gap-3">
          <div className="text-xs text-slate-500 mr-auto">
            {Object.keys(answers).length} / {questions.length} answered
          </div>
          <Button
            onClick={submit}
            disabled={!allAnswered || submitting}
            className="rounded-full bg-sky-500 hover:bg-sky-600 px-6"
            data-testid="tassess-submit"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Grading…</> : "Submit"}
          </Button>
        </div>
      </div>
    </StudentLayout>
  );
}

function ResultScreen({ result, navigate }) {
  const pct = Math.round((result.score / result.max_score) * 100);
  const passed = result.score >= Math.ceil(result.max_score * 0.6);
  return (
    <StudentLayout>
      <div className="max-w-2xl mx-auto" data-testid="tassess-result">
        <Button variant="ghost" size="sm" onClick={() => navigate("/student/tuitions")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Tuitions
        </Button>

        <div className={`rounded-2xl p-6 mb-6 ${passed ? "bg-emerald-50 dark:bg-emerald-900/30" : "bg-amber-50 dark:bg-amber-900/30"}`}>
          <div className="flex items-center gap-3">
            <Trophy className={`h-8 w-8 ${passed ? "text-emerald-600" : "text-amber-600"}`} />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Your score</div>
              <div className="text-3xl font-bold text-slate-900 dark:text-slate-100" data-testid="tassess-score">
                {result.score} / {result.max_score} <span className="text-base text-slate-500 font-normal">({pct}%)</span>
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                {passed ? "Great work — keep practising!" : "Almost there — review the explanations below and try again."}
              </div>
            </div>
          </div>
        </div>

        <ol className="space-y-4">
          {result.details.map((q, i) => (
            <li key={q.question_id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`tassess-detail-${i}`}>
              <div className="flex items-start gap-2">
                {q.is_correct ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 text-rose-500 mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold mb-2">{i + 1}. {q.prompt}</div>
                  {q.figure_image && (
                    <img src={`${API_BASE}${q.figure_image}`} alt="figure" className="mb-2 max-w-[300px] max-h-[240px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white" />
                  )}
                  <ul className="space-y-1.5">
                    {q.options.map((opt, idx) => {
                      const isCorrect = idx === q.correct_index;
                      const isChosen = idx === q.selected;
                      const tone =
                        isCorrect ? "text-emerald-700 dark:text-emerald-300 font-medium" :
                        isChosen ? "text-rose-600 dark:text-rose-400 line-through" :
                        "text-slate-600 dark:text-slate-400";
                      return (
                        <li key={idx} className={`text-sm ${tone}`}>
                          {String.fromCharCode(65 + idx)}. {opt}
                          {isCorrect && <span className="ml-2 text-xs text-emerald-600">✓ correct</span>}
                          {isChosen && !isCorrect && <span className="ml-2 text-xs text-rose-500">your answer</span>}
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-semibold text-slate-600 dark:text-slate-300">Why: </span>{q.explanation}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </StudentLayout>
  );
}
